export function format(first?: string, middle?: string, last?: string): string {
  return (first || '') + (middle ? ` ${middle}` : '') + (last ? ` ${last}` : '');
}

/**
 * Extracts access token from cookies and formats it as "Bearer <token>"
 * @returns Formatted bearer token string or empty string if token not found
 */
export const getBearerTokenFromCookies = (): string => {
  // Get all cookies
  const cookies = document.cookie;
  
  // Find the access token cookie
  const cookieArray = cookies.split(';');
  const accessTokenCookie = cookieArray.find(cookie => 
    cookie.trim().startsWith('accessToken=')
  );
  
  if (!accessTokenCookie) {
    return '';
  }
  
  // Extract the token value
  const token = accessTokenCookie.split('=')[1];
  
  // Decode any percent-encoded characters
  let decodedToken = decodeURIComponent(token);
  
  // Format as "Bearer <token>"
  decodedToken = decodedToken.replace(/^["']+|["']+$/g, '');

  // Ensure exactly one space after 'Bearer' (fixes cases like "Bearer    token")
  decodedToken = decodedToken.replace(/Bearer\s*/, 'Bearer ');
  return decodedToken;
}

// Constants for image URLs
const STATIC_IMAGE_URL = 'https://photos.le-offers.com';
const STATIC_VIDEO_URL = 'https://asset.le-offers.com';
// Type definition for image URL object
export interface ImageUrl {
  url?: string;
  thumbnailUrl?: string;
  type?: string;
}

/**
 * Extracts and resolves image source URL based on image object and thumbnail preference
 * @param imageUrl - Object containing url, thumbnailUrl, and type, or array of such objects, or string
 * @param isNeedThumbnail - Whether to prefer thumbnail URL (default: true)
 * @returns Resolved image URL string
 */
export const getImageSrc = (
  imageUrl?: { url?: string; thumbnailUrl?: string; type?: string } | { url?: string; thumbnailUrl?: string; type?: string }[] | string,
  isNeedThumbnail: boolean = true
): string => {
  if (!imageUrl) return '';
 
  // Handle array case - take the first element
  let actualImageUrl: { url?: string; thumbnailUrl?: string; type?: string } | string;
  if (Array.isArray(imageUrl)) {
    actualImageUrl = imageUrl[0] || '';
  } else {
    actualImageUrl = imageUrl;
  }
 
  // If it's a string, return it directly (with URL resolution if needed)
  if (typeof actualImageUrl === 'string') {
    if (!actualImageUrl) return '';
    return actualImageUrl.startsWith('http') || actualImageUrl.startsWith('data:') 
      ? actualImageUrl 
      : `${STATIC_IMAGE_URL}/${actualImageUrl}`;
  }
 
  // Handle object case
  const { url, thumbnailUrl, type } = actualImageUrl;
 
  const resolveUrl = (path?: string): string => {
    if (!path) return '';
    return path.startsWith('http') || path.startsWith('data:') ? path : `${STATIC_IMAGE_URL}/${path}`;
  };
  const resolveThumbnailUrl = (path?: string): string => {
    if (!path) return '';
    return path.startsWith('http') || path.startsWith('data:') ? path : `${STATIC_VIDEO_URL}/${path}`;
  };
 
  if (type === 'VIDEO' && isNeedThumbnail) {
    if(!thumbnailUrl){
      return resolveThumbnailUrl(url);
    }
    return resolveUrl(thumbnailUrl);
  }
 
  return resolveUrl(url);
};

/**
 * Safely extracts product image from data that can be either an array or a string
 * @param productImage - Product image data that can be an array of images or a single image string
 * @returns The first image from array or the string itself, or null if no image
 */
export const getProductImage = (productImage?: string[] | string | null): string | null => {
  if (!productImage) return null;
  
  if (Array.isArray(productImage)) {
    return productImage[0] || null;
  }
  
  return productImage;
};
